import { createContext, type PropsWithChildren, useContext } from 'react';

import { GRID_CELL_HEIGHT, MIN_TILE_HEIGHT_IN_CELLS, TILE_OUTER_PADDING } from '@/config/grid.ts';
import { store } from '@/store/store.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

const observer = new ResizeObserver((entries) => {
  const tileHeights = tilesSlice.selectors.selectTileHeights(store.getState());

  for (const entry of entries) {
    const {
      target,
      contentRect: { height },
    } = entry;
    if (!(target instanceof HTMLElement)) {
      continue;
    }

    const { parentElement } = target;
    if (!parentElement?.dataset.tileId) {
      console.error('TileHeightObserver: observed element is not inside a tile with data-tile-id attribute');
      continue;
    }

    const tileHeightInCells = Math.max(
      MIN_TILE_HEIGHT_IN_CELLS,
      Math.ceil(
        (height + 2 * TILE_OUTER_PADDING) / // this padding is part of the computation as it takes spaces in cells
          GRID_CELL_HEIGHT,
      ),
    );

    if (tileHeights[parentElement.dataset.tileId] !== tileHeightInCells) {
      store.dispatch(
        tilesSlice.actions.resizeTile({
          tileId: parentElement.dataset.tileId,
          h: tileHeightInCells,
        }),
      );
    }
  }
});

const tileHeightObserverContext = createContext(observer);

export const TileHeightObserverProvider = ({ children }: PropsWithChildren) => (
  <tileHeightObserverContext.Provider value={observer}>{children}</tileHeightObserverContext.Provider>
);

export const useTileHeightObserver = () => useContext(tileHeightObserverContext);
